import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertCircle,
  FileText,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';
import { IContact } from 'types';
import {
  getContactAttributeActivityRequest,
  selectContactAttributeActivity,
  selectActivityLoading,
  selectActivityError,
  selectActivityPagination,
} from 'store/activity';

const AttributesTab = ({ contact }: { contact: IContact }) => {
  const dispatch = useDispatch();
  const activities = useSelector(selectContactAttributeActivity);
  const loading = useSelector(selectActivityLoading);
  const error = useSelector(selectActivityError);
  const pagination = useSelector(selectActivityPagination);
  const [expandedChanges, setExpandedChanges] = useState<Set<string>>(
    new Set()
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && pagination) {
      const hasPaginationData =
        pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0;
      if (hasPaginationData) {
        if (
          currentPage !== pagination.page ||
          currentLimit !== pagination.limit
        ) {
          setCurrentPage(pagination.page);
          setCurrentLimit(pagination.limit);
        }
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized, currentPage, currentLimit]);

  useEffect(() => {
    if (isInitialized && contact._id) {
      dispatch(
        getContactAttributeActivityRequest(
          contact._id,
          currentPage,
          currentLimit
        )
      );
    }
  }, [contact._id, currentPage, currentLimit, dispatch, isInitialized]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatValue = (value: unknown) => {
    if (value === null || value === 'null') return 'null';
    if (value === '') return '(empty)';
    return String(value);
  };

  const getChangeType = (from: unknown, to: unknown) => {
    if (from === '' && to !== '') return 'added';
    if (from !== '' && to === '') return 'removed';
    if (from !== to) return 'updated';
    return 'unchanged';
  };

  const getChangeBadgeClass = (changeType: string) => {
    switch (changeType) {
      case 'added':
        return 'bg-success-background border-success/30 text-green-800';
      case 'removed':
        return 'bg-destructive-background border-destructive/30 text-red-800';
      case 'updated':
        return 'bg-primary-background border-primary/30 text-gray-900';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const toggleExpanded = (changeId: string) => {
    setExpandedChanges((prev) => {
      const next = new Set(prev);
      if (next.has(changeId)) next.delete(changeId);
      else next.add(changeId);
      return next;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedChanges(new Set());
  };

  const handleLimitChange = (limit: number) => {
    setCurrentLimit(limit);
    setCurrentPage(0);
    setExpandedChanges(new Set());
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Attribute Changes
          </h3>
          <p className="text-xs text-gray-500">
            Track changes to contact attributes over time
          </p>
        </div>
        {!loading && activities && activities.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Show:
              </span>
              <div className="relative">
                <select
                  value={currentLimit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="appearance-none bg-white border border-gray-200 rounded-md px-3 py-2 pr-7 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer"
                  aria-label="Items per page"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                per page
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {pagination.total} change{pagination.total !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Content card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-4 text-center text-gray-500">
            <Loader2 className="w-5 h-5 mx-auto mb-2 text-gray-400 animate-spin" />
            <p className="text-sm font-medium">Loading changes...</p>
            <p className="text-xs">Fetching attribute activity</p>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-destructive">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-destructive/50" />
            <p className="text-sm font-medium">Error loading changes</p>
            <p className="text-xs">{error}</p>
          </div>
        ) : !activities?.length ? (
          <div className="p-4 text-center text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium">No changes yet</p>
            <p className="text-xs">Attribute changes will appear here</p>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="bg-muted/50 border-b border-border px-3 py-2.5">
              <div className="grid grid-cols-12 gap-3 text-xs font-medium uppercase tracking-wide">
                <div className="col-span-1" aria-hidden="true" />
                <div className="col-span-7">Changes</div>
                <div className="col-span-4">Date</div>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {activities.map((change) => {
                const isExpanded = expandedChanges.has(change._id);
                const changeCount = Object.keys(change.attrs).length;

                return (
                  <div key={change._id}>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(change._id)}
                      className="w-full px-3 py-3 hover:bg-gray-50 transition-colors text-left flex items-center gap-3"
                      aria-expanded={isExpanded}
                      aria-controls={`attr-details-${change._id}`}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-background border border-primary/20 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-7">
                          <span className="text-sm font-medium text-gray-900">
                            {changeCount} attribute
                            {changeCount !== 1 ? 's' : ''} changed
                          </span>
                        </div>
                        <div className="col-span-4 flex items-center gap-1.5 text-xs text-gray-500">
                          <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                          {formatDate(change.createdAt)}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div
                        id={`attr-details-${change._id}`}
                        className="bg-muted/30 border-t border-border"
                        role="region"
                        aria-label="Change details"
                      >
                        <div className="px-3 py-3">
                          <div className="space-y-2">
                            {Object.entries(change.attrs).map(
                              ([attrName, changeData]) => {
                                const changeType = getChangeType(
                                  changeData.from,
                                  changeData.to
                                );
                                return (
                                  <div
                                    key={attrName}
                                    className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 bg-white rounded-md border border-border"
                                  >
                                    <span className="text-sm font-medium text-gray-700">
                                      {attrName}
                                    </span>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs text-gray-500">
                                        From:{' '}
                                        <code className="font-mono text-gray-700 bg-muted px-1.5 py-0.5 rounded">
                                          {formatValue(changeData.from)}
                                        </code>
                                      </span>
                                      <span
                                        className="text-gray-400 text-xs"
                                        aria-hidden="true"
                                      >
                                        →
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        To:{' '}
                                        <code className="font-mono text-gray-700 bg-muted px-1.5 py-0.5 rounded">
                                          {formatValue(changeData.to)}
                                        </code>
                                      </span>
                                      <span
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getChangeBadgeClass(changeType)}`}
                                      >
                                        {changeType}
                                      </span>
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {!loading &&
        activities &&
        activities.length > 0 &&
        pagination.total > 0 && (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-gray-500">
              {pagination.page * pagination.limit + 1}–
              {Math.min(
                (pagination.page + 1) * pagination.limit,
                pagination.total
              )}{' '}
              of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagination.page === 0}
                onClick={() => handlePageChange(pagination.page - 1)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-500 px-2 py-1">
                Page {pagination.page + 1} of{' '}
                {Math.max(1, pagination.totalPages)}
              </span>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages - 1}
                onClick={() => handlePageChange(pagination.page + 1)}
                className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default AttributesTab;
